import dayjs from 'dayjs';
import { EYES, POWER_COLUMNS } from '../../utils/glasses';

// The printable glasses prescription: an A5 slip with the doctor's header, the
// patient, and the powers for each eye. Printed by the eye doctor from the
// visit and by the optician from the order, so both hold the same paper.
//
// Opens a print window with the slip as static HTML, the way the lab sheet
// does, rather than hiding a portal in the page: the visit page already has a
// hidden prescription sheet for window.print().
//
//   spec        the glasses prescription (visit.glasses or order.spec)
//   patient     { name, dateOfBirth, gender, phoneNumber }
//   doctorInfo  the prescribing doctor's letterhead fields
//   date        when it was prescribed
//   lang        'en' | 'ar' | 'ku'
//   t           the translator
// Returns false when the browser blocked the pop-up.

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const buildGlassesPrescriptionHtml = ({ spec = {}, patient, doctorInfo = {}, date, lang = 'en', t }) => {
    const rtl = lang === 'ar' || lang === 'ku';
    const tr = (key, fallback) => {
        const value = t ? t(key) : null;
        return value && value !== key ? value : fallback;
    };

    const age = patient?.dateOfBirth
        ? dayjs().diff(dayjs(patient.dateOfBirth), 'year')
        : (patient?.age || '-');

    const rows = EYES.map(([eye, labelKey, fallback]) => `
        <tr>
            <td class="eye">${escapeHtml(tr(labelKey, fallback))}</td>
            ${POWER_COLUMNS.map(([suffix]) => `<td class="value">${escapeHtml(spec[`${eye}${suffix}`] || '')}</td>`).join('')}
        </tr>`).join('');

    return `<!DOCTYPE html>
<html lang="${lang}" dir="${rtl ? 'rtl' : 'ltr'}">
<head>
<meta charset="utf-8">
<title>${escapeHtml(tr('glassesPrescription', 'Glasses prescription'))} - ${escapeHtml(patient?.name || '')}</title>
<style>
    @page { size: A5 landscape; margin: 10mm; }
    * { box-sizing: border-box; }
    body {
        font-family: 'Segoe UI', Tahoma, Arial, sans-serif;
        color: #1f2a37;
        margin: 0;
        font-size: 11pt;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    .sheet { width: 188mm; margin: 0 auto; }
    header {
        display: flex; justify-content: space-between; align-items: flex-start;
        border-bottom: 2px solid #05668D; padding-bottom: 3mm; margin-bottom: 3mm;
    }
    .clinic h1 { font-size: 15pt; margin: 0 0 1mm; color: #05668D; }
    .clinic div { font-size: 9.5pt; color: #4a5a68; }
    .doc { text-align: end; font-size: 9.5pt; color: #4a5a68; }
    .doc strong { display: block; font-size: 11pt; color: #1f2a37; }
    .title { text-align: center; font-size: 12.5pt; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; margin: 0 0 3mm; color: #05668D; }
    .patient {
        display: grid; grid-template-columns: repeat(4, 1fr); gap: 2mm 6mm;
        background: #f3f7fa; border-radius: 2mm; padding: 2.5mm 4mm; margin-bottom: 4mm; font-size: 10pt;
    }
    .patient span { color: #6b7a87; font-size: 8.5pt; display: block; }
    /* The powers read left to right in every language, as they are written. */
    table { width: 100%; border-collapse: collapse; font-size: 11pt; direction: ltr; }
    th, td { padding: 2.5mm 3mm; text-align: center; border: 1px solid #c9d4dc; }
    th { font-size: 9pt; letter-spacing: 0.06em; color: #05668D; background: #e9f1f6; font-weight: 600; }
    td.eye { font-weight: 600; text-align: start; width: 46mm; background: #f7fafc; }
    td.value { font-weight: 600; font-variant-numeric: tabular-nums; height: 11mm; }
    .extras { display: flex; gap: 8mm; margin-top: 3mm; font-size: 10.5pt; }
    .extras span { color: #6b7a87; font-size: 9pt; margin-inline-end: 2mm; }
    .note { font-size: 10pt; color: #4a5a68; margin: 3mm 0 0; white-space: pre-wrap; }
    .sign { margin-top: 10mm; text-align: end; font-size: 10pt; }
    .sign div { display: inline-block; border-top: 1px solid #1f2a37; padding-top: 1.5mm; min-width: 50mm; text-align: center; }
</style>
</head>
<body>
<div class="sheet">
    <header>
        <div class="clinic">
            <h1>${escapeHtml(doctorInfo.clinicName || doctorInfo.name || '')}</h1>
            ${doctorInfo.clinicAddress ? `<div>${escapeHtml(doctorInfo.clinicAddress)}</div>` : ''}
            ${doctorInfo.clinicPhone ? `<div>${escapeHtml(doctorInfo.clinicPhone)}</div>` : ''}
        </div>
        <div class="doc">
            <strong>${escapeHtml(doctorInfo.name || '')}</strong>
            ${doctorInfo.specialization ? `<div>${escapeHtml(doctorInfo.specialization)}</div>` : ''}
        </div>
    </header>

    <div class="title">${escapeHtml(tr('glassesPrescription', 'Glasses prescription'))}</div>

    <div class="patient">
        <div><span>${escapeHtml(tr('patientName', 'Patient'))}</span>${escapeHtml(patient?.name || '-')}</div>
        <div><span>${escapeHtml(tr('age', 'Age'))} / ${escapeHtml(tr('gender', 'Sex'))}</span>${escapeHtml(age)} / ${escapeHtml(patient?.gender ? tr(patient.gender, patient.gender) : '-')}</div>
        <div><span>${escapeHtml(tr('date', 'Date'))}</span>${date ? dayjs(date).format('YYYY-MM-DD') : '-'}</div>
        <div><span>${escapeHtml(tr('phoneNumber', 'Phone'))}</span>${escapeHtml(patient?.phoneNumber || '-')}</div>
    </div>

    <table>
        <thead>
            <tr>
                <th></th>
                ${POWER_COLUMNS.map(([, label]) => `<th>${label}</th>`).join('')}
            </tr>
        </thead>
        <tbody>${rows}</tbody>
    </table>

    <div class="extras">
        ${spec.pd ? `<div><span>${escapeHtml(tr('pupillaryDistance', 'PD (mm)'))}</span><b>${escapeHtml(spec.pd)}</b></div>` : ''}
        ${spec.lensType ? `<div><span>${escapeHtml(tr('lensType', 'Lens type'))}</span><b>${escapeHtml(spec.lensType)}</b></div>` : ''}
    </div>

    ${spec.note ? `<p class="note">${escapeHtml(spec.note)}</p>` : ''}

    <div class="sign"><div>${escapeHtml(tr('doctorSignature', 'Signature'))}</div></div>
</div>
</body>
</html>`;
};

export const printGlassesPrescription = (args) => {
    const html = buildGlassesPrescriptionHtml(args);
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return false;
    win.document.open();
    win.document.write(html);
    win.document.close();
    // Give the window a moment to lay out before the print dialog measures it.
    win.focus();
    setTimeout(() => {
        try {
            win.print();
        } catch (error) {
            console.error('Print failed:', error);
        }
    }, 250);
    return true;
};

export default printGlassesPrescription;
